import React, { useEffect, useMemo, useState } from "react";

const SearchMultiSelect = ({
  label,
  placeholder = "Buscar...",
  value = [],
  onChange,
  options: providedOptions = [],
  onSearch,
  getOptionLabel,
  getOptionKey,
  allowCreate = false,
  createLabel = (query) => `Agregar ${query}`,
  onCreateOption,
  disabled = false,
}) => {
  const [query, setQuery] = useState("");
  const [asyncOptions, setAsyncOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const selectedIds = useMemo(
    () => new Set((value || []).map((item) => String(item.id))),
    [value]
  );

  useEffect(() => {
    if (!onSearch || !query.trim()) {
      setAsyncOptions([]);
      return;
    }
    let active = true;
    setLoading(true);
    const timer = setTimeout(async () => {
      try {
        const results = await onSearch(query.trim());
        if (!active) return;
        setAsyncOptions(results || []);
      } catch (error) {
        if (active) {
          setAsyncOptions([]);
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [query, onSearch]);

  const handleSelect = (option) => {
    if (!option) return;
    const next = [...(value || [])];
    if (!selectedIds.has(String(option.id))) {
      next.push(option);
      onChange?.(next);
    }
    setQuery("");
    setOpen(false);
  };

  const handleRemove = (id) => {
    const next = (value || []).filter((item) => String(item.id) !== String(id));
    onChange?.(next);
  };

  const handleCreate = () => {
    if (!allowCreate || !onCreateOption) return;
    const created = onCreateOption(query.trim());
    if (created) {
      handleSelect(created);
    }
  };

  const mergedOptions = useMemo(() => {
    const map = new Map();
    (providedOptions || []).forEach((option) => {
      if (!option) return;
      map.set(String(option.id), option);
    });
    (asyncOptions || []).forEach((option) => {
      if (!option) return;
      map.set(String(option.id), option);
    });
    return Array.from(map.values());
  }, [providedOptions, asyncOptions]);

  const visibleOptions = (mergedOptions || []).filter(
    (option) => !selectedIds.has(String(option.id))
  ).filter((option) => {
    const normalized = String(query || "").trim().toLowerCase();
    if (!normalized) return true;
    const labelText = String(option.label || getOptionLabel?.(option) || option.id).toLowerCase();
    return labelText.includes(normalized);
  });

  return (
    <div className="relative">
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      <div className="flex flex-wrap gap-2 mb-2">
        {(value || []).map((item) => (
          <span
            key={String(item.id)}
            className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-700 px-2 py-1 text-xs"
          >
            {item.label || getOptionLabel?.(item) || item.id}
            <button
              type="button"
              onClick={() => handleRemove(item.id)}
              className="text-gray-500 hover:text-gray-800"
            >
              ✕
            </button>
          </span>
        ))}
      </div>
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (visibleOptions.length > 0) {
              handleSelect(visibleOptions[0]);
            } else if (allowCreate) {
              handleCreate();
            }
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
      />
      {open && (query.trim() || loading || visibleOptions.length > 0) && (
        <div className="absolute z-40 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-64 overflow-y-auto">
          {loading && (
            <div className="px-3 py-2 text-xs text-gray-500">Buscando...</div>
          )}
          {!loading && visibleOptions.length === 0 && allowCreate && query.trim() && (
            <button
              type="button"
              onClick={handleCreate}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
            >
              {createLabel(query.trim())}
            </button>
          )}
          {!loading && visibleOptions.length === 0 && !allowCreate && query.trim() && (
            <div className="px-3 py-2 text-xs text-gray-500">Sin resultados</div>
          )}
          {visibleOptions.map((option) => (
            <button
              type="button"
              key={getOptionKey ? getOptionKey(option) : String(option.id)}
              onClick={() => handleSelect(option)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
            >
              {option.label || getOptionLabel?.(option) || option.id}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchMultiSelect;
